import React from 'react'
import {
  BRCC,
  THEME_BASE_BLACK,
  THEME_BASE_WHITE,
  BRCC_YELLOW,
  THEME_BRCC_YELLOW,
} from 'theme/constants'

import { BRCC_PROJECT_ROUTE } from 'routes/constants'

import BRCCLogo from 'images/index/brcc.svg'

// BRCC Images
import BRCCHeroRight from 'images/projects/brcc/brcc-hero-right.webp'
import BRCCHeroLeft from 'images/projects/brcc/brcc-hero-left.avif'
import BRCCLeftOne from 'images/projects/brcc/brcc-image-left-1.avif'
import BRCCLeftOneMobile from 'images/projects/brcc/brcc-image-left-1-mobile.webp'
import BRCCWorkOne from 'images/projects/brcc/brcc-work-image.avif'
import BRCCWorkOneMobile from 'images/projects/brcc/brcc-work-image-mobile.webp'
import BRCCBefore from 'images/projects/brcc/brcc-before-image.webp'
import BRCCAfter from 'images/projects/brcc/brcc-after-image.avif'
import BRCCBeforeAfterMobile from 'images/projects/brcc/brcc-before-after-mobile.webp'
import BRCCFullWidthOne from 'images/projects/brcc/brcc-full-width-1.avif'
import BRCCFullWidthOneMobile from 'images/projects/brcc/brcc-full-width-1-mobile.webp'
import BRCCFullWidthTwo from 'images/projects/brcc/brcc-full-width-2.avif'
import BRCCFullWidthTwoMobile from 'images/projects/brcc/brcc-full-width-2-mobile.webp'
import BRCCFullWidthThree from 'images/projects/brcc/brcc-full-width-3.webp'
import BRCCFullWidthThreeMobile from 'images/projects/brcc/brcc-full-width-3-mobile.webp'
import BRCCFullWidthFour from 'images/projects/brcc/brcc-full-width-4.avif'
import BRCCFullWidthFourMobile from 'images/projects/brcc/brcc-full-width-4-mobile.webp'

const data = {
  id: BRCC,
  name: 'Black Rifle Coffee Company',
  route: BRCC_PROJECT_ROUTE,
  general: {
    logo: <BRCCLogo />,
    tagline: 'Serving coffee and culture',
  },
  theme: {
    primary: THEME_BASE_BLACK,
  },
  colors: {
    primary: BRCC_YELLOW,
  },
  // Todo: make theme object and use primary and secondary instead of hardcoding theme variables
  hero: {
    theme: BRCC,
    logo: <BRCCLogo />,
    slides: [
      {
        text: 'Serving coffee and culture',
        images: [
          { key: 'left', image: BRCCHeroLeft },
          { key: 'right', image: BRCCHeroRight },
        ],
      },
    ],
  },
  overview: {
    isClientStudio: false,
    theme: THEME_BRCC_YELLOW,
    titleTheme: THEME_BASE_BLACK,
    client: 'Black Rifle Coffee Company',
    sector: 'Retail, Food & Beverage, E-commerce, Loyalty Program',
    year: '2022',
    paragraphs: [
      'Black Rifle Coffee Company approached us to build a next-generation loyalty program to activate and retain high value customers.',
      'At the time, their loyalty program was disparate across channels, clunky to redeem, and confusing for users. Our goal was to unify loyalty across all consumer touch points, consider other avenues of community like events and media, and make redemption flexible and clear to customers.',
    ],
  },
  overviewImage: {
    image: {
      source: BRCCFullWidthFour,
      desktop: BRCCFullWidthFour,
      mobile: BRCCFullWidthFourMobile,
      alt: 'Black Rifle Coffee Company Loyalty Website Dashboard',
    },
  },
  strategyOne: {
    theme: THEME_BASE_WHITE,
    title: 'BRCC Loyalty: A Community Cornerstone',
    paragraphs: [
      'When designing a loyalty program for a brand with an avid consumer base, careful cost benefit modeling and a competitor review of loyalty mechanisms are crucial early steps.',
      'As we modeled out potential strategies, we identified key transactions and value-adds to ensure incentives aligned with BRCC business goals.',
    ],
    image: {
      source: BRCCFullWidthThree,
      desktop: BRCCFullWidthThree,
      mobile: BRCCFullWidthThreeMobile,
      alt: 'Black Rifle Coffee Company Strategy Flow Chart Diagram',
    },
  },
  strategyTwo: {
    title: 'Program Implementation',
    paragraphs: [
      'Third party loyalty program vendors were considered as we outlined an implementation guide and designed the visual translation of the program across app, web, email, and in-person experiences.',
    ],
  },
  work: {
    theme: 'brcc-custom-gradient',
    titleTheme: THEME_BRCC_YELLOW,
    description: 'Unite the BRCC community via two-tier reward system.',
    paragraphs: [
      'Two-tier loyalty programs serve community focused brands by rewarding diehard fans but retaining simplicity for casual customers. Tiers create recognition amongst the community while points provide tangible value.',
    ],
    image: {
      source: BRCCWorkOne,
      desktop: BRCCWorkOne,
      mobile: BRCCWorkOneMobile,
      alt: 'Black Rifle Coffee Company loyalty rewards list page',
    },
  },
  rank: {
    theme: THEME_BASE_WHITE,
    description: 'Ranks that utilize brand storytelling.',
    paragraphs: [
      'Cultivate long term brand stickiness through progressive ranks. Tenure-based ranks motivate high-value customers to stay engaged over the long term. Rank titles and mascots lean in to BRCC’s unique character storytelling opportunities.',
    ],
    image: {
      source: BRCCFullWidthTwo,
      desktop: BRCCFullWidthTwo,
      mobile: BRCCFullWidthTwoMobile,
      alt: 'Different Member Ranks of Black Rifle Coffee Company',
    },
  },
  customSectionTwo: {
    theme: 'brcc-custom-gradient',
    imageLeft: {
      source: BRCCBefore,
      alt: 'Black Rifle Coffee Company website account dashboard before',
    },
    imageRight: {
      source: BRCCAfter,
      alt: 'Black Rifle Coffee Company website account dashboard after',
    },
    mobile: {
      source: BRCCBeforeAfterMobile,
      alt: 'Black Rifle Coffee Company website account dashboard before & after',
    },
  },
  customSectionOne: {
    theme: THEME_BASE_BLACK,
    image: {
      source: BRCCFullWidthOne,
      desktop: BRCCFullWidthOne,
      mobile: BRCCFullWidthOneMobile,
      alt: 'Black Rifle Coffee bags & Member Rank cards',
    },
  },
  twoTextImageColumns: [
    {
      theme: THEME_BASE_BLACK,
      title: 'Loyalty no matter where you shop',
      paragraphs: [
        'The mobile app and web portal bring forward a loyalty experience to help the user redeem the right rewards at the right time and place. From brick and mortar, to e-commerce on web or app, all purchases contribute to status.',
      ],
      image: {
        source: BRCCLeftOne,
        desktop: BRCCLeftOne,
        mobile: BRCCLeftOneMobile,
        alt: 'Black Rifle Coffee Company Loyalty Cards',
      },
      isImageOnLeft: true,
    },
  ],
}

export default data
